import { useRef, useEffect, useState } from "react";
import { ChevronLeft, ChevronRightIcon, XIcon, FileText } from "lucide-react";
import { usePdfRenderer } from "hooks/usePdfRenderer";
import { pdfFileUrl } from "../utils/fetchResearchReport";

export const PageViewerPanel = ({
  projectId,
  file,
  page,
  maxPage,
  onPageChange,
  onClose,
}: {
  projectId: string;
  file: string;
  page: number;
  maxPage: number;
  onPageChange: (page: number) => void;
  onClose: () => void;
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { renderPage } = usePdfRenderer();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setLoading(true);

    const containerWidth = containerRef.current?.clientWidth || 800;

    const renderPdfPage = async () => {
      const url = await pdfFileUrl(projectId, file);
      const result = await renderPage(canvas, url, page, { containerWidth });
      if (result) setLoading(false);
    };

    renderPdfPage();
  }, [file, page, projectId, renderPage]);

  return (
    <div className="w-1/2 shrink-0 border-l border-gray-200 bg-white flex flex-col">
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-gray-50 shrink-0">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-default"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-xs text-gray-600 font-medium min-w-[100px] text-center truncate">
            {file} — p.{page}
            {maxPage > 0 ? `/${maxPage}` : ""}
          </span>
          <button
            type="button"
            onClick={() =>
              onPageChange(Math.min(maxPage || page + 1, page + 1))
            }
            disabled={maxPage > 0 && page >= maxPage}
            className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-default"
          >
            <ChevronRightIcon size={16} />
          </button>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded hover:bg-gray-200 text-gray-500"
        >
          <XIcon size={16} />
        </button>
      </div>
      <div
        ref={containerRef}
        className="flex-1 overflow-auto bg-gray-100 relative"
      >
        {loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gray-100 z-10">
            <div className="relative">
              <div className="w-16 h-20 bg-white rounded-lg shadow-sm border border-gray-200 flex items-center justify-center">
                <FileText size={28} className="text-gray-300" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
              </div>
            </div>
            <span className="text-xs text-gray-400 font-medium">
              Loading page...
            </span>
          </div>
        )}
        <div className="flex items-start justify-center p-2">
          <canvas ref={canvasRef} className="shadow-md" />
        </div>
      </div>
    </div>
  );
};
